import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const AddonsTab = ({
  addonForm,
  addons,
  categories,
  deleteAddon,
  editingAddon,
  handleAddonSubmit,
  isAddonDialogOpen,
  openEditAddonDialog,
  resetAddonForm,
  setAddonForm,
  setIsAddonDialogOpen,
}) => (
          <TabsContent value="addons">
            <div className="space-y-6">
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase">Add-ons / Extras</CardTitle>
                  <Dialog open={isAddonDialogOpen} onOpenChange={setIsAddonDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                        onClick={resetAddonForm}
                        data-testid="add-addon-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Addon
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141417] border-[#27272A] max-w-md">
                      <DialogHeader>
                        <DialogTitle>{editingAddon ? 'Edit Addon' : 'New Addon'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddonSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={addonForm.name}
                            onChange={(e) => setAddonForm({...addonForm, name: e.target.value})}
                            required
                            placeholder="e.g. Extra Cheese"
                            className="bg-[#0B0B0C] border-[#27272A]"
                            data-testid="addon-form-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Price (CHF)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={addonForm.price}
                            onChange={(e) => setAddonForm({...addonForm, price: e.target.value})}
                            required
                            placeholder="2.00"
                            className="bg-[#0B0B0C] border-[#27272A]"
                            data-testid="addon-form-price"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select
                            value={addonForm.category_id}
                            onValueChange={(value) => setAddonForm({...addonForm, category_id: value})}
                          >
                            <SelectTrigger className="bg-[#0B0B0C] border-[#27272A]" data-testid="addon-form-category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {cat.name_en || cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={addonForm.is_active}
                            onChange={(e) => setAddonForm({...addonForm, is_active: e.target.checked})}
                            className="rounded"
                            data-testid="addon-form-active"
                          />
                          <Label>Active</Label>
                        </div>
                        <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="addon-form-submit">
                          {editingAddon ? 'Update Addon' : 'Create Addon'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {addons.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">No add-ons yet. Create your first add-on!</p>
                  ) : (
                    <div className="space-y-3">
                      {categories.filter(cat => addons.some(a => a.category_id === cat.id)).map(cat => (
                        <div key={cat.id} className="mb-4">
                          <h3 className="text-sm font-semibold text-[#FFC107] uppercase tracking-wider mb-2">
                            {cat.name_en || cat.name}
                          </h3>
                          <Table>
                            <TableHeader>
                              <TableRow className="border-[#27272A]">
                                <TableHead>Name</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {addons.filter(a => a.category_id === cat.id).map(addon => (
                                <TableRow key={addon.id} className="border-[#27272A]">
                                  <TableCell>
                                    <div>
                                      <p className="font-semibold">{addon.name}</p>
                                      {addon.name_it && <p className="text-xs text-zinc-500">IT: {addon.name_it}</p>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-[#FFC107]">{formatPrice(addon.price)}</TableCell>
                                  <TableCell>
                                    <Badge className={addon.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}>
                                      {addon.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-zinc-400 hover:text-white"
                                        onClick={() => openEditAddonDialog(addon)}
                                        data-testid={`edit-addon-${addon.id}`}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-400 hover:text-red-300"
                                        onClick={() => deleteAddon(addon.id)}
                                        data-testid={`delete-addon-${addon.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default AddonsTab;
